import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type PointerEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { createManualMoment, fetchFilm, fetchMoments } from '../../lib/queries.ts';
import type { Film } from '../../lib/types.ts';

export function ManualMomentPanel({
  film,
  onCreated,
  onClose,
}: {
  film: Film;
  onCreated: () => void;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const filmDur = film.durationSec != null ? Number(film.durationSec) : 0;
  const filmDetail = useQuery({ queryKey: ['film', film.id], queryFn: () => fetchFilm(film.id) });
  const presignedUrl = filmDetail.data?.presignedUrl ?? null;

  const existingMomentsQuery = useQuery({
    queryKey: ['moments', film.id],
    queryFn: () => fetchMoments(film.id),
  });
  const existing = existingMomentsQuery.data?.moments ?? [];

  const [startSec, setStartSec] = useState(0);
  const [endSec, setEndSec] = useState(Math.min(60, filmDur));
  const [title, setTitle] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [dragSide, setDragSide] = useState<'start' | 'end' | null>(null);

  const valid = startSec < endSec && endSec <= filmDur;

  const mutation = useMutation({
    mutationFn: () => createManualMoment(film.id, { startSec, endSec, title: title || undefined }),
    onSuccess: () => onCreated(),
  });

  function pctFromPointer(e: PointerEvent): number {
    const r = timelineRef.current!.getBoundingClientRect();
    return Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
  }

  function onTimelinePointerDown(e: PointerEvent): void {
    if (dragSide) return;
    const pct = pctFromPointer(e);
    const sec = pct * filmDur;
    if (videoRef.current) videoRef.current.currentTime = sec;
  }

  function onMarkerPointerDown(side: 'start' | 'end', e: PointerEvent): void {
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setDragSide(side);
  }
  function onMarkerPointerMove(e: PointerEvent): void {
    if (!dragSide) return;
    const sec = pctFromPointer(e) * filmDur;
    if (dragSide === 'start') setStartSec(Math.min(sec, endSec - 0.1));
    else setEndSec(Math.max(sec, startSec + 0.1));
  }
  function onMarkerPointerUp(): void { setDragSide(null); }

  useEffect(() => {
    if (filmDur > 0 && endSec === 0) setEndSec(Math.min(60, filmDur));
  }, [filmDur, endSec]);

  return (
    <div style={{ padding: 16, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--bg-card)', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>{t('moments.manual.title')}</h3>
        <button type="button" onClick={onClose}>{t('common.cancel')}</button>
      </div>

      {presignedUrl ? (
        <video ref={videoRef} src={presignedUrl} controls style={{ width: '100%', maxHeight: '40vh', background: '#000' }} />
      ) : (
        <div style={{ padding: 16, color: 'var(--text-3)' }}>{t('moments.manual.noSource')}</div>
      )}

      <div
        ref={timelineRef}
        onPointerDown={onTimelinePointerDown}
        onPointerMove={onMarkerPointerMove}
        onPointerUp={onMarkerPointerUp}
        style={{ position: 'relative', height: 32, background: 'var(--bg-elev)', borderRadius: 4, margin: '12px 0', cursor: 'pointer', userSelect: 'none' }}
      >
        {existing.map((m) => {
          const left = (Number(m.startSec) / filmDur) * 100;
          const width = ((Number(m.endSec) - Number(m.startSec)) / filmDur) * 100;
          const color = m.strategy === 'custom' ? 'rgba(34,197,94,0.30)' : 'rgba(59,130,246,0.30)';
          return (
            <div
              key={m.id}
              style={{ position: 'absolute', left: `${left}%`, top: 0, width: `${width}%`, bottom: 0, background: color, pointerEvents: 'none' }}
              title={m.title}
            />
          );
        })}
        <Marker side="start" sec={startSec} filmDur={filmDur} onPointerDown={onMarkerPointerDown} />
        <Marker side="end" sec={endSec} filmDur={filmDur} onPointerDown={onMarkerPointerDown} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{t('moments.manual.start')}</span>
          <input type="text" value={formatHms(startSec)} onChange={(e) => setStartSec(parseHms(e.target.value, startSec))} style={{ fontFamily: 'var(--mono)', width: 80 }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{t('moments.manual.end')}</span>
          <input type="text" value={formatHms(endSec)} onChange={(e) => setEndSec(parseHms(e.target.value, endSec))} style={{ fontFamily: 'var(--mono)', width: 80 }} />
        </label>
        <button type="button" onClick={() => videoRef.current && setStartSec(videoRef.current.currentTime)}>
          {t('moments.manual.setStartFromPlayhead')}
        </button>
        <button type="button" onClick={() => videoRef.current && setEndSec(videoRef.current.currentTime)}>
          {t('moments.manual.setEndFromPlayhead')}
        </button>
        <button type="button" onClick={() => { setStartSec(0); setEndSec(filmDur); }}>
          {t('moments.manual.wholeFilm')}
        </button>
      </div>

      <div style={{ marginTop: 12 }}>
        <input
          type="text"
          placeholder={t('moments.manual.titlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>

      {mutation.error && (
        <div style={{ color: 'var(--hot)', fontSize: 12, marginTop: 8 }}>
          {(mutation.error as Error).message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button type="button" onClick={onClose}>{t('common.cancel')}</button>
        <button
          type="button"
          className="primary"
          disabled={!valid || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? t('moments.manual.creating') : t('moments.manual.add')}
        </button>
      </div>
    </div>
  );
}

function Marker({
  side, sec, filmDur, onPointerDown,
}: {
  side: 'start' | 'end';
  sec: number;
  filmDur: number;
  onPointerDown: (side: 'start' | 'end', e: PointerEvent) => void;
}): ReactElement {
  const left = filmDur > 0 ? (sec / filmDur) * 100 : 0;
  const color = side === 'start' ? '#22d3ee' : '#fb923c';
  return (
    <div
      onPointerDown={(e) => onPointerDown(side, e)}
      style={{
        position: 'absolute',
        left: `${left}%`,
        top: -4,
        bottom: -4,
        width: 3,
        background: color,
        cursor: 'ew-resize',
        zIndex: 2,
      }}
      title={`${side}: ${formatHms(sec)}`}
    />
  );
}

function formatHms(sec: number): string {
  const total = Math.floor(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}
function parseHms(input: string, fallback: number): number {
  const parts = input.split(':').map((p) => Number(p));
  if (parts.some((n) => !Number.isFinite(n))) return fallback;
  if (parts.length === 3) return parts[0]! * 3600 + parts[1]! * 60 + parts[2]!;
  if (parts.length === 2) return parts[0]! * 60 + parts[1]!;
  if (parts.length === 1) return parts[0]!;
  return fallback;
}
